import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PushCard } from '../../components/PushCard';
import { ErrorNote, Loading, Toggle } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { NOTIFICATION_LABELS } from '../../lib/notifications';
import { notificationsApi } from '../../lib/resources';
import type { NotificationSettings, NotificationType } from '../../types';

/** Configurações › Notificações — preferências de quem está logado (Fase 6 / N2). */
export function NotificationSettingsSection() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['notifications', 'settings'],
    queryFn: notificationsApi.settings,
  });
  const save = useMutation({
    mutationFn: (patch: Partial<NotificationSettings>) =>
      notificationsApi.updateSettings(patch),
    onSuccess: (next) => queryClient.setQueryData(['notifications', 'settings'], next),
  });

  if (settings.isLoading || !settings.data) return <Loading />;
  const data = settings.data;

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="border-b border-slate-200 px-5 py-4">
        <h2 className="text-base font-semibold text-slate-900">Notificações</h2>
        <p className="text-xs text-slate-500">
          Suas preferências (valem só para você). Os avisos chegam no sino do CRM e, se você
          ativar abaixo, também no celular.
        </p>
      </header>
      <div className="space-y-5 p-5">
        <PushCard />
        <Toggle
          label="Receber notificações"
          checked={data.enabled}
          disabled={save.isPending}
          onChange={(enabled) => save.mutate({ enabled })}
        />
        <Toggle
          label="Mostrar pop-up na tela"
          hint="Um aviso no canto da tela quando algo chega"
          checked={data.popup}
          disabled={save.isPending || !data.enabled}
          onChange={(popup) => save.mutate({ popup })}
        />
        <div
          className={`space-y-3 border-t border-slate-100 pt-4 ${data.enabled ? '' : 'opacity-50'}`}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Avisar quando
          </p>
          {(Object.keys(NOTIFICATION_LABELS) as NotificationType[]).map((type) => (
            <Toggle
              key={type}
              label={NOTIFICATION_LABELS[type]}
              checked={data.events[type] ?? true}
              disabled={save.isPending || !data.enabled}
              onChange={(value) =>
                save.mutate({ events: { [type]: value } as Record<NotificationType, boolean> })
              }
            />
          ))}
        </div>
        {save.isError && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
      </div>
    </section>
  );
}
